import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getFeePayments,
  getPaymentById,
  getStudentDues,
  recordCashPayment,
  getPaymentReceipt,
  downloadReceiptPdf,
} from '../api/fee-payments.api'
import type {
  GetFeePaymentsDto,
  RecordCashPaymentDto,
  FeePaymentResponseDto,
} from '../types/fee-payment.dto'
// Import the UI model types from the component — type-only, no circular runtime dep
import type { TransactionRecord, PaymentModeType } from '../components/PaymentsTab'

export const FEE_PAYMENTS_QUERY_KEY = 'feePayments'
export const STUDENT_DUES_QUERY_KEY = 'studentDues'
export const PAYMENT_RECEIPTS_QUERY_KEY = 'paymentReceipts'

// ── Anti-corruption mapping layer ─────────────────────────────────────────
// This is the ONLY place that translates backend DTOs → UI models.
// UI components must never consume raw backend DTOs directly.

/** Normalize backend payment mode codes to the UI's PaymentModeType */
function normalizePaymentMode(code: string): PaymentModeType {
  const upper = code.toUpperCase()
  if (upper === 'CASH') return 'Cash'
  if (upper === 'UPI' || upper === 'ONLINE') return 'UPI'
  if (upper === 'CHEQUE') return 'Cheque'
  if (upper === 'BANK_TRANSFER' || upper === 'NEFT' || upper === 'RTGS') return 'Bank Transfer'
  return 'Cash'
}

/** Normalize backend status codes to the UI TransactionRecord status */
function normalizeStatus(code: string): TransactionRecord['status'] {
  const upper = code.toUpperCase()
  if (upper === 'SUCCESS' || upper === 'COMPLETED') return 'Success'
  if (upper === 'FAILED' || upper === 'DECLINED') return 'Failed'
  return 'Pending'
}

/** Format an ISO date string to the UI's display format: "Aug 12, 2023" */
function formatDisplayDate(iso: string): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    })
  } catch {
    return iso
  }
}

/** Map a single FeePaymentResponseDto → TransactionRecord (UI model) */
function toTransactionRecord(dto: FeePaymentResponseDto): TransactionRecord {
  return {
    id: dto.id,
    receiptNo: dto.receiptNo,
    studentName: dto.studentName ?? 'Unknown',
    // Display admission no as the student identifier (human-readable)
    studentId: dto.studentAdmissionNo ?? dto.studentId ?? 'N/A',
    // className is session-scoped and not on the payment record; show dash
    className: '—',
    date: formatDisplayDate(dto.paymentDate),
    // Detailed fee breakdown lives in the receipt; show generic label here
    feeType: 'Fee Payment',
    amount: dto.amountPaid,
    mode: normalizePaymentMode(dto.paymentMode),
    status: normalizeStatus(dto.status),
    referenceNo: dto.txnReference,
  }
}

// ── Hooks ──────────────────────────────────────────────────────────────────

export const useGetFeePayments = (params?: GetFeePaymentsDto) => {
  return useQuery({
    queryKey: [FEE_PAYMENTS_QUERY_KEY, params],
    queryFn: () => getFeePayments(params),
  })
}

/**
 * Mapped version of useGetFeePayments.
 * Returns paginated TransactionRecord[] — the exact UI model for PaymentsTab
 * history screen. Prefer this hook in UI components over useGetFeePayments.
 */
export const useGetFeePaymentsAsTxns = (params?: GetFeePaymentsDto) => {
  const query = useGetFeePayments(params)
  const raw = query.data as any
  const rawItems: FeePaymentResponseDto[] = Array.isArray(raw?.items)
    ? raw.items
    : Array.isArray(raw?.data?.items)
    ? raw.data.items
    : Array.isArray(raw?.data)
    ? raw.data
    : Array.isArray(raw)
    ? raw
    : []

  return {
    ...query,
    data: query.data
      ? {
          ...(typeof raw === 'object' && raw !== null ? raw : {}),
          items: rawItems.map(toTransactionRecord),
        }
      : undefined,
  }
}

export const useGetPaymentById = (id: string) => {
  return useQuery({
    queryKey: [FEE_PAYMENTS_QUERY_KEY, id],
    queryFn: () => getPaymentById(id),
    enabled: !!id,
  })
}

export const useGetStudentDues = (studentId: string, params?: Record<string, unknown>) => {
  return useQuery({
    queryKey: [STUDENT_DUES_QUERY_KEY, studentId, params],
    queryFn: () => getStudentDues(studentId, params),
    enabled: !!studentId,
  })
}

export const useRecordCashPayment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: RecordCashPaymentDto) => recordCashPayment(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [FEE_PAYMENTS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [STUDENT_DUES_QUERY_KEY, variables.studentId] })
    },
  })
}

export const useGetPaymentReceipt = (id: string) => {
  return useQuery({
    queryKey: [PAYMENT_RECEIPTS_QUERY_KEY, id],
    queryFn: () => getPaymentReceipt(id),
    enabled: !!id,
  })
}

export const useDownloadReceiptPdf = () => {
  return useMutation({
    mutationFn: (id: string) => downloadReceiptPdf(id),
    onSuccess: (blob, id) => {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `receipt_${id}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
  })
}
